import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import { Card, Button } from "reactstrap";
import Head from "../../../layout/head/Head";
import { Table, Input, Select, Tag, Tooltip, Flex, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";
import { Icon } from "../../../components/Component";
import dayjs from "dayjs";
import tenderApis from "../../../api/tender/tenderApis";

const { Option } = Select;

const SupplierDetailsPage: React.FC = () => {
  const { tenderId } = useParams<{ tenderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as any;
  const [tenderNumber, setTenderNumber] = useState(locationState?.tenderNumber || "");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<any[]>([]);

  const debouncedSetSearch = React.useCallback(
    debounce((value: string) => {
      setSearch(value);
    }, 500),
    []
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearch(e.target.value);
  };

  const fetchBidResponses = async () => {
    if (!tenderId) return;
    setLoading(true);
    try {
      const res = await tenderApis.fetchBidResponses(Number(tenderId), {
        sortOrder: "DESC",
        direction: "F",
        pageSize: 100,
        pageDiff: 0,
        searchQuery: search || undefined,
        bidStatus: status !== "All" ? status : undefined,
      });
      if (res?.status && res?.data?.list) {
        setDataSource(res.data.list);
        if (res.data.tenderNumber && !tenderNumber) {
          setTenderNumber(res.data.tenderNumber);
        }
      }
    } catch (error) {
      console.error("Error fetching bid responses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBidResponses();
  }, [tenderId, search, status]);

  const noWrapHeader = () => ({
    style: { whiteSpace: "nowrap" as const },
  });

  const columns = [
    {
      title: "Supplier Id",
      dataIndex: "bidNumber",
      key: "bidNumber",
      onHeaderCell: noWrapHeader,
      render: (text: string, record: any) => (
        <Tooltip title="Click to view supplier details">
          <span
            className="inlineTableCellLink"
            onClick={() =>
              navigate(
                `/tender-evaluation/${tenderId}/supplier/${record.supplierBidId}`,
                {
                  state: {
                    bidId: record.supplierBidId,
                    bidType: "Supplier",
                    tenderId: Number(tenderId),
                  },
                }
              )
            }
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Supplier / Contractor Name",
      dataIndex: "supplierName",
      key: "supplierName",
      onHeaderCell: noWrapHeader,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      onHeaderCell: noWrapHeader,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      onHeaderCell: noWrapHeader,
      render: (text: string) =>
        text && text.length > 30 ? (
          <Tooltip title={text}>
            <span>{text.slice(0, 30)}...</span>
          </Tooltip>
        ) : (
          <span>{text || "--"}</span>
        ),
    },
    {
      title: "Submission Date",
      dataIndex: "submittedAt",
      key: "submittedAt",
      onHeaderCell: noWrapHeader,
      render: (text: any) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "--",
    },
    {
      title: "Contact Mail",
      dataIndex: "contactEmail",
      key: "contactEmail",
      onHeaderCell: noWrapHeader,
    },
    {
      title: "Sub Total",
      dataIndex: "subTotal",
      key: "subTotal",
      onHeaderCell: noWrapHeader,
      render: (text: any, record: any) =>
        text != null
          ? `${record.currency || ""} ${Number(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--",
    },
    {
      title: "Total Installation Charges",
      dataIndex: "totalInstallationCharges",
      key: "totalInstallationCharges",
      onHeaderCell: noWrapHeader,
      render: (text: any, record: any) =>
        text != null
          ? `${record.currency || ""} ${Number(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--",
    },
    {
      title: "Taxable Amount",
      dataIndex: "netValue",
      key: "netValue",
      onHeaderCell: noWrapHeader,
      render: (text: any, record: any) =>
        text != null
          ? `${record.currency || ""} ${Number(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--",
    },
    {
      title: "Tax",
      dataIndex: "taxValue",
      key: "taxValue",
      onHeaderCell: noWrapHeader,
      render: (text: any, record: any) =>
        text != null
          ? `${record.currency || ""} ${Number(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--",
    },
    {
      title: "Total",
      dataIndex: "grossValue",
      key: "grossValue",
      onHeaderCell: noWrapHeader,
      render: (text: any, record: any) =>
        text != null
          ? `${record.currency || ""} ${Number(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--",
    },
    {
      title: "Past Awards",
      dataIndex: "pastAwardsCount",
      key: "pastAwardsCount",
      onHeaderCell: noWrapHeader,
      render: (text: any) => <span>{text ?? 0}</span>,
    },
    {
      title: "Status",
      dataIndex: "bidStatus",
      key: "bidStatus",
      onHeaderCell: noWrapHeader,
      render: (text: string) => {
        const colorMap: Record<string, string> = {
          Submitted: "gold",
          Evaluated: "green",
          Rejected: "red",
        };
        return (
          <Tag color={colorMap[text] || "default"} style={{ fontWeight: 500 }}>
            {text}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <Head title="Supplier/Contractor Details" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap" style={{ overflow: "hidden" }}>
            <div className="card-inner card-inner-lg" style={{ overflow: "hidden" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <h5 style={{ fontWeight: 600, fontSize: 20, margin: 0 }}>
                  Supplier/Contractor Details for {tenderNumber || tenderId}
                </h5>
                <Button
                  color="primary"
                  onClick={() => navigate("/tender-evaluation")}
                >
                  <Icon name="arrow-left" />
                  <span>Back</span>
                </Button>
              </div>
              <div style={{ marginBottom: 32, overflowX: "auto" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                    padding: "0 0 12px 0",
                  }}
                >
                  <div>
                    <label style={{ fontWeight: 500, marginRight: 8 }}>
                      STATUS
                    </label>
                    <Select
                      value={status}
                      onChange={setStatus}
                      style={{ minWidth: 180 }}
                    >
                      <Option value="All">All</Option>
                      <Option value="Submitted">Submitted</Option>
                    </Select>
                  </div>
                  <div>
                    <Input
                      placeholder="Search by any field"
                      style={{ width: 300 }}
                      allowClear
                      prefix={<SearchOutlined />}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                {loading ? (
                  <Flex
                    align="center"
                    justify="center"
                    style={{ minHeight: 200 }}
                  >
                    <Spin size="large" />
                  </Flex>
                ) : (
                  <Table
                    columns={columns}
                    dataSource={dataSource.map((item) => ({
                      ...item,
                      key: item.supplierBidId,
                    }))}
                    pagination={false}
                    bordered
                    style={{ background: "white" }}
                    locale={{
                      emptyText: (
                        <span style={{ color: "#888" }}>
                          No bid responses found.
                        </span>
                      ),
                    }}
                    scroll={{ x: 1800 }}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default SupplierDetailsPage;
